import { z } from "zod";
import { shoppingSummary } from "./shopping.ts";

export const paymentPilotParams = z.object({ id: z.string().uuid() });
export type PaymentPilotParams = z.infer<typeof paymentPilotParams>;
export const paymentPilotActionParams = paymentPilotParams.extend({
  token: z.string().max(8192),
});
export type PaymentPilotActionParams = z.infer<typeof paymentPilotActionParams>;

export const pilotPurchaseSchema = z.object({
  attemptId: z.string().uuid(),
  merchant: z.string(),
  item: z.string(),
  amountMinor: z.number().int().positive(),
  currency: z.literal("usd"),
  checkoutSessionId: z.string(),
  checkoutUrl: z.string().url(),
});
export type PilotPurchase = z.infer<typeof pilotPurchaseSchema>;

export const paymentAttemptStatusSchema = z.enum([
  "preparing",
  "missingDetails",
  "awaitingConfirmation",
  "authorizing",
  "awaitingProvider",
  "ready",
  "creatingBrowser",
  "submitting",
  "verifying",
  "authenticationRequired",
  "orderPending",
  "paid",
  "subscriptionActive",
  "cancelled",
  "declined",
  "expired",
  "outcomeUnknown",
  "recoveryRequired",
  "needsAttention",
]);
export type PaymentAttemptStatus = z.infer<typeof paymentAttemptStatusSchema>;

export const paymentPilotSummarySchema = z.object({
  id: z.string().uuid(),
  threadId: z.number().int(),
  status: paymentAttemptStatusSchema,
  purchase: pilotPurchaseSchema
    .pick({ merchant: true, item: true, amountMinor: true, currency: true })
    .nullable(),
  actionPath: z.string().optional(),
  receiptUrl: z.string().optional(),
  expiresAt: z.string(),
  message: z.string(),
  /** Present for merchant purchases; absent for legacy pilot attempts. */
  shopping: shoppingSummary.optional(),
});
export type PaymentPilotSummary = z.infer<typeof paymentPilotSummarySchema>;
export const createPaymentPilotReq = z.object({
  threadId: z.number().int().positive(),
  idempotencyKey: z.string().uuid(),
});
export const approvePaymentPilotReq = z.object({
  paymentMethodId: z.string().min(1).max(255),
  billingName: z.string().trim().min(1).max(255),
  email: z.string().email().max(255),
  billingCountry: z
    .string()
    .trim()
    .regex(/^[A-Za-z]{2}$/)
    .transform((value) => value.toUpperCase()),
  billingPostalCode: z.string().trim().max(32).optional(),
});
export type ApprovePaymentPilotReq = z.infer<typeof approvePaymentPilotReq>;
